import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';

const formatDt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
};

const shortUserAgent = (ua) => {
  if (!ua) return 'Desconocido';
  const s = ua.trim();
  if (/iPhone|iPad|iPod/i.test(s)) return 'iPhone / iPad';
  if (/Android/i.test(s)) return 'Android';
  if (/Chrome/i.test(s) && !/Edg/i.test(s)) return 'Chrome';
  if (/Safari/i.test(s) && !/Chrome/i.test(s)) return 'Safari';
  if (/Firefox/i.test(s)) return 'Firefox';
  if (/Edg/i.test(s)) return 'Edge';
  if (s.length > 48) return `${s.slice(0, 48)}…`;
  return s;
};

const SupervisorDevices = () => {
  const [supervisorFactories, setSupervisorFactories] = useState([]);
  const [factoryId, setFactoryId] = useState('');
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await attendanceAPI.supervisorSites();
        setSupervisorFactories(res.data || []);
      } catch {
        setSupervisorFactories([]);
      }
    })();
  }, []);

  const loadRows = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const params = {};
      if (factoryId) params.factory_id = Number(factoryId);
      if (employeeUserId) params.employee_user_id = Number(employeeUserId);
      const res = await attendanceAPI.supervisorTeamDevices(params);
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron cargar los dispositivos');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [factoryId, employeeUserId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const employeeOptions = useMemo(() => {
    return rows
      .map((r) => ({ id: r.employee_user_id, name: r.employee_name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  useEffect(() => {
    if (!employeeUserId) return;
    if (!employeeOptions.some((o) => String(o.id) === String(employeeUserId))) {
      setEmployeeUserId('');
    }
  }, [employeeUserId, employeeOptions]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.employee_name?.toLowerCase().includes(q) ||
        r.employee_email?.toLowerCase().includes(q) ||
        (r.factory_names || []).some((n) => n.toLowerCase().includes(q))
    );
  }, [rows, search]);

  /** Una fila de tabla por dispositivo (o una fila “sin dispositivos” por empleado). */
  const tableRows = useMemo(() => {
    const out = [];
    for (const person of filteredRows) {
      if (!person.devices?.length) {
        out.push({
          key: `${person.employee_user_id}-none`,
          person,
          device: null,
        });
        continue;
      }
      for (const d of person.devices) {
        out.push({
          key: `${person.employee_user_id}-${d.device_id}`,
          person,
          device: d,
        });
      }
    }
    return out;
  }, [filteredRows]);

  const totalDevices = useMemo(
    () => tableRows.filter((r) => r.device).length,
    [tableRows]
  );

  return (
    <div className="space-y-3 pb-4">
      <p className="text-xs text-gray-600 leading-snug">
        Cada fila es un dispositivo donde la persona inició sesión: tipo de navegador/celular, fechas,
        cantidad de logins e IP del último acceso.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex-1 min-w-[140px]">
          <label className="form-label-mobile text-xs">Sede</label>
          <select
            className="input-mobile w-full"
            value={factoryId}
            onChange={(e) => setFactoryId(e.target.value)}
          >
            <option value="">Todas mis sedes</option>
            {supervisorFactories.map((f) => (
              <option key={f.id} value={String(f.id)}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="form-label-mobile text-xs">Empleado</label>
          <select
            className="input-mobile w-full"
            value={employeeUserId}
            onChange={(e) => setEmployeeUserId(e.target.value)}
          >
            <option value="">Todos</option>
            {employeeOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="form-label-mobile text-xs">Buscar</label>
          <input
            type="search"
            className="input-mobile w-full"
            placeholder="Nombre o email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button type="button" className="btn-mobile btn-secondary min-h-[44px]" onClick={loadRows}>
          Actualizar
        </button>
      </div>

      {err && <div className="alert-mobile error">{err}</div>}

      <p className="text-xs text-gray-500">
        {loading
          ? 'Cargando…'
          : `${filteredRows.length} persona(s) · ${totalDevices} dispositivo(s)`}
      </p>

      {!loading && tableRows.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-xs text-gray-600">
          No hay personas bajo tu supervisión con los filtros actuales, o aún no iniciaron sesión.
        </div>
      )}

      {tableRows.length > 0 && (
        <div className="card-mobile p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left min-w-[580px]">
              <thead>
                <tr className="text-gray-500 border-b border-slate-200 bg-slate-50">
                  <th className="py-2 px-2 font-medium whitespace-nowrap">Empleado</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap">Email</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap">Dispositivo</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap">Último login</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap">Primera vez</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap">IP</th>
                  <th className="py-2 px-2 font-medium whitespace-nowrap text-center">Logins</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map(({ key, person, device }) => (
                  <tr key={key} className="border-b border-slate-100 hover:bg-slate-50/80">
                    <td className="py-2 px-2 font-medium text-gray-900 whitespace-nowrap">
                      {person.employee_name}
                    </td>
                    <td className="py-2 px-2 text-gray-600 max-w-[140px] truncate" title={person.employee_email}>
                      {person.employee_email}
                    </td>
                    {!device ? (
                      <td colSpan={5} className="py-2 px-2 text-gray-400 italic">
                        Sin dispositivos registrados
                      </td>
                    ) : (
                      <>
                        <td className="py-2 px-2 text-gray-800 whitespace-nowrap">
                          {shortUserAgent(device.user_agent)}
                        </td>
                        <td className="py-2 px-2 text-gray-700 tabular-nums whitespace-nowrap">
                          {formatDt(device.last_login_at)}
                        </td>
                        <td className="py-2 px-2 text-gray-600 tabular-nums whitespace-nowrap">
                          {formatDt(device.first_seen_at)}
                        </td>
                        <td className="py-2 px-2 text-gray-600 font-mono text-[11px] whitespace-nowrap">
                          {device.ip_address || '—'}
                        </td>
                        <td className="py-2 px-2 text-center text-gray-800 tabular-nums">
                          {device.login_count}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorDevices;
